/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';

import { ContentBlock } from '@/components/wpl/content-block/src/content-block';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Row } from '@/libraries/wpl/foundations/src/grid';
import { IconNameTypes } from '@/libraries/wpl/foundations/src/icon';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

interface Card {
	icon: IconNameTypes;
	text: string | ReactNode;
}

const Eyelash = () => (
	<svg
		width="40"
		height="25"
		viewBox="0 0 40 25"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M23.632 8.42251L24.7544 7.4286C24.7544 7.4286 25.0032 6.84209 24.8041 6.44563C24.6825 6.20123 24.4945 5.96228 24.29 5.77762C23.8753 5.40832 23.3832 5.28879 23.1675 5.86991C23.1399 5.85362 23.0957 5.85364 23.057 5.82648C22.9796 6.5651 22.0506 6.60315 21.7133 7.15168C21.846 7.24401 21.7023 7.17336 21.7023 7.17336C21.752 7.75447 21.1881 8.0858 21.1881 8.09123C20.4527 9.20458 19.1035 10.4483 18.4456 11.768L18.4732 11.7788L18.4954 11.8006C18.7387 11.8223 18.3848 12.1482 18.3461 12.0178C17.5665 12.4143 16.5159 13.6145 16.3611 14.4618C16.527 14.5704 16.4385 14.717 16.3943 14.6518C15.8358 14.6138 11.4014 19.3496 13.7734 19.2519L13.9172 19.4148C14.2434 19.165 16.4993 17.2479 16.5159 17.2696C16.9693 17.5031 16.8863 15.3959 17.3563 16.3517C19.8389 15.8955 22.9187 10.3234 22.9187 10.3234L22.9906 9.66079C22.9906 9.66079 23.8863 9.11767 23.6264 8.41708L23.632 8.42251Z"
			fill="#101214"
		/>
		<path
			d="M4.81616 4.90336L4.57284 4.28422C4.57284 4.28422 5.11469 3.39894 4.55624 2.89385L5.08709 1.50356C5.08709 1.50356 5.03179 0.868118 4.6724 0.60743C4.4457 0.444501 4.16923 0.314139 3.90383 0.248967C3.3675 0.107761 2.86988 0.22186 2.94729 0.840992C2.91411 0.840992 2.87536 0.857229 2.82559 0.851798C3.10205 1.54153 2.29484 1.99232 2.25614 2.63318C2.41649 2.6549 2.53811 2.70378 2.44965 2.70378L2.2506 2.66036C2.56577 3.15458 1.86904 3.28491 2.47725 3.80085L2.22293 3.70307C2.09023 5.02823 1.47094 6.73358 1.50964 8.20537H1.54285C1.54285 8.20537 1.63132 8.57468 1.54285 8.47692C1.03416 9.18295 0.669234 10.7199 0.918048 11.54C1.1171 11.5617 -1.19963 18.1224 0.857235 16.9656L1.06181 17.047C1.23322 16.6777 1.37142 16.3682 1.86352 16.0478C1.8414 15.9826 1.88565 15.988 1.67001 15.9445C1.64237 15.9228 1.58157 15.8848 1.49311 15.8739C0.973362 15.7762 2.68736 12.7729 2.68736 12.7729C4.67788 11.2467 4.81063 4.9088 4.81063 4.9088L4.81616 4.90336Z"
			fill="#101214"
		/>
		<path
			d="M39.8603 17.4274C39.6723 16.9115 39.3019 16.5748 38.8264 16.9875C38.8098 16.9604 38.7711 16.9386 38.749 16.8952C38.3232 17.5089 37.4883 17.107 36.9298 17.4383C37.0017 17.5795 36.9077 17.4546 36.9077 17.4546C36.6699 17.9868 36.0175 18.0194 36.0175 18.0248C34.8287 18.6657 33.0428 19.1273 31.8319 19.9854L31.8485 20.0071L31.8595 20.0343C32.0641 20.1646 31.5942 20.2895 31.6218 20.1538C30.7427 20.1375 29.2387 20.7077 28.6968 21.3757C28.7908 21.5495 28.8406 21.0607 29.1503 21.6201C28.9733 21.7016 28.8296 21.8645 28.6526 21.9297C28.4868 21.5495 28.6415 21.6364 28.6305 21.5604C28.155 21.2671 21.9678 23.3744 24.1021 24.39L24.1519 24.6018C24.561 24.5312 25.4236 24.3682 25.3849 24.2867C25.1747 23.8142 27.4749 23.8903 27.4804 23.9229L28.6637 23.5047C31.0689 24.2542 36.4709 20.7892 36.4709 20.7892L36.8579 20.2407C36.8579 20.2407 37.9085 20.181 38.0191 19.4423L39.4843 19.0893C39.4843 19.0893 39.9875 18.6874 39.9986 18.2475C40.0096 17.9759 39.9598 17.6772 39.8713 17.422L39.8603 17.4274Z"
			fill="#101214"
		/>
	</svg>
);

const Stars = () => (
	<svg width="126" height="64" viewBox="0 0 126 64" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M126 15.5613C125.593 13.5037 125.727 16.089 124.977 15.7532C124.682 17.3843 122.812 18.3865 122.421 19.6232C122.635 20.0389 122.753 20.2734 122.346 20.5933C121.773 20.0229 121.023 20.9931 120.3 22.1498C119.63 23.573 117.348 26.5368 117.208 25.7425C117.198 25.1082 113.64 31.0517 113.994 29.3033C112.494 30.3907 110.902 31.8673 109.241 33.5464C109.075 32.5922 108.904 31.6487 108.732 30.7265C108.716 30.7532 108.732 30.8331 108.684 30.7745C108.47 31.2542 109.466 33.6689 108.368 34.4419C107.94 31.3449 106.322 20.0122 108.256 28.3171C105.389 20.8012 120.08 20.3747 124.302 15.5507C126.6 11.4195 111.417 19.7831 110.125 20.54C110.125 20.54 110.195 20.2361 110.195 20.2308C103.498 24.4898 108.116 7.07517 101.96 6.0144C99.2548 7.10715 98.4297 11.3342 97.235 13.7116C97.4814 13.6156 98.0279 11.4035 98.8476 11.1583C98.2904 12.2991 96.0938 17.896 96.8599 14.9056C91.6845 30.2468 87.0502 29.4845 78.7994 33.3864C78.3762 33.5144 56.7314 41.3555 59.228 43.8022C60.5942 42.9014 62.8016 42.7521 63.8999 41.8086C63.9213 41.3289 63.9427 41.0623 64.4838 40.9557C65.116 42.4802 69.6111 38.5251 70.1254 38.4717C70.3397 39.6338 78.0922 34.6711 76.6671 36.2222C80.2888 35.4333 84.8857 33.5357 89.6539 31.5474C88.0252 38.8769 84.3767 44.234 84.3767 44.234C84.3767 44.234 73.6079 39.6817 64.1999 42.4323C68.9789 44.5165 78.344 43.6796 83.9535 46.4834C79.6781 58.2105 76.8867 67.3896 79.887 61.2542L79.962 61.67C80.9425 57.9546 83.6909 54.3353 84.1142 50.7159C83.4338 49.0527 85.1643 49.5165 85.8715 47.1497C87.8323 48.3011 94.7437 48.7169 91.5613 51.5261C89.0378 55.7265 81.8747 59.2766 79.6031 63.9941C83.7927 63.7223 88.5878 58.0186 91.4327 54.9696C91.4327 54.9696 91.4809 55.2734 91.4809 55.2787C93.3507 53.9621 94.958 50.1295 97.6582 52.1444C97.6529 52.1444 97.1761 52.1497 97.1653 52.1497C101.221 53.3811 111.893 67.0805 111.968 56.201C111.636 50.0282 109.343 37.0325 109.343 37.0325C109.343 37.0325 111.915 32.9867 113.399 32.5975C114.701 31.5954 125.952 20.396 125.989 15.5613H126ZM94.1383 25.5026C95.2366 22.3363 96.1849 19.8097 95.7938 21.9792C97.8297 18.6903 99.5602 13.0506 101.96 10.3267C103.991 15.3374 105.175 23.1413 105.175 23.1413C105.175 23.1413 98.8155 25.8705 93.565 27.1498C93.7579 26.5954 93.9454 26.041 94.1329 25.5026H94.1383ZM87.4198 45.3374C87.4198 45.3374 90.2915 36.009 91.3898 32.1924C91.3898 32.2724 91.5506 30.3534 91.5559 30.4227C91.5559 30.4227 101.719 27.2777 105.261 25.7212C105.888 29.1114 106.482 32.5229 107.007 35.8811C103.053 39.9803 96.0724 48.3598 96.0724 48.3598C96.0724 48.3598 90.479 47.299 87.4198 45.3428V45.3374ZM109 56.2329C109 56.3395 109 57.0591 108.781 56.6806C108.791 56.7233 108.802 56.7659 108.813 56.8086C108.797 56.7713 108.775 56.8086 108.781 56.6753C100.487 50.5559 93.6186 51.6167 104.093 42.6988C103.6 42.7574 101.596 44.5059 101.585 44.4685C101.885 42.9014 107.988 38.0986 104.746 41.9259C105.529 41.2916 106.514 40.396 107.538 39.3886C108.309 45.0069 109.091 50.7212 109 56.2276V56.2329Z"
			fill="#101214"
		/>
		<path
			d="M45.1481 15.604L45.1053 15.3694C44.3391 16.1903 42.5175 16.4676 42.0782 17.2885C42.1853 17.7309 42.3354 17.9014 41.8907 18.0774C38.7618 20.4601 33.9668 22.9121 30.5057 25.1669C30.1253 23.7276 29.252 21.0464 29.252 21.0464C29.1395 21.4036 28.9842 19.33 28.8824 18.669C30.0503 18.5837 31.154 18.5731 32.1559 18.685L32.1023 18.5997C32.1505 18.6211 32.2738 18.5624 32.322 18.6637C35.349 17.0859 38.8261 16.9739 42.2979 15.54C43.8784 15.8439 47.9233 13.4664 43.214 13.8396C40.7281 14.6605 37.8082 13.8662 35.4937 15.2362C35.4937 15.2362 35.5741 14.9696 35.5741 14.9643C32.6059 15.1295 27.6394 17.6456 27.4519 12.9974C27.2697 13.0827 27.8055 13.5571 27.2215 13.8183C27.1197 13.3599 26.9322 12.9174 26.8518 12.4803C27.2537 12.4004 27.1679 12.6775 27.2537 12.7149C27.4251 12.8908 25.7482 -2.56761 24.2695 0.364158L24.0444 0.300195C24.0712 0.806591 24.0873 1.27033 24.0712 1.75007C23.2676 0.96649 22.914 2.02722 22.2818 3.43979C22.0889 5.23617 20.8835 6.62213 20.337 8.33855C20.6799 8.44516 20.4816 8.54111 20.3155 8.40784C20.278 9.67117 19.4101 10.1509 19.6565 11.4196C19.6565 11.4196 19.4637 11.2117 19.4583 11.2064C18.3654 13.4505 18.1243 18.0614 14.7061 18.072C14.7865 18.2533 15.3436 17.7522 15.5847 18.3492C15.0758 18.4238 14.5775 18.5891 14.0953 18.6477C14.0418 18.2426 14.3364 18.3439 14.3793 18.2639C14.4811 18.2586 -4.801 18.9089 1.13525 22.4004C2.65681 22.7202 3.63727 23.9888 5.03025 24.6285C5.19098 24.3033 5.22847 24.5112 5.08381 24.6552C6.17677 24.7831 6.43395 25.6466 7.58048 25.4974C7.58048 25.4974 7.36616 25.6733 7.3608 25.6733C10.72 27.0699 13.6131 28.6157 11.4058 32.4004C11.6094 32.4164 11.4487 32.8801 11.4058 32.8002C10.8808 33.6424 5.50172 44.8364 6.53574 47.5602C6.69647 49.0101 7.63941 48.2479 9.01632 47.1392C11.7648 43.8716 13.806 43.7916 15.3169 41.0784C15.3169 41.0784 15.3865 41.3556 15.3919 41.3609C17.7439 39.9696 20.9906 34.7404 24.0284 35.1829C25.3517 35.3588 34.7758 44.9803 34.69 42.3204L34.915 42.2458C34.7436 41.8886 34.6633 39.6605 34.3632 38.4558C32.8095 35.2948 33.6667 33.6424 32.0326 31.6541C32.0326 31.6541 32.3059 31.686 32.3112 31.686C31.2987 29.5645 31.1379 27.459 33.3131 26.1637C33.147 26.0517 32.8684 26.8087 32.3434 26.4302C32.7881 26.0677 33.1792 25.6413 33.6078 25.3108C33.8596 25.6306 33.5328 25.7159 33.5274 25.8119C33.7096 25.9985 48.8127 15.3108 45.1481 15.6093V15.604ZM25.3624 29.298C22.6247 33.2479 19.378 28.7703 16.1044 27.475C17.026 24.9857 17.926 22.3843 18.9279 20.0976C21.071 20.3694 25.8125 17.6136 26.4286 20.087C27.3126 23.8716 29.5038 27.1978 25.3624 29.2927V29.298ZM20.9692 16.281L20.8674 16.2917C20.9102 16.2597 20.9209 16.1104 21.0335 16.1157C21.3549 11.6914 22.2979 9.33536 24.1409 4.95903C26.0803 8.86628 24.4248 12.2511 25.5285 16.4302C23.3533 16.9206 23.0533 16.862 20.4656 17.0646C20.6316 16.7927 20.7977 16.5315 20.9692 16.281ZM12.0487 24.6712C9.34312 23.9356 6.70182 22.9334 3.91585 21.3982C4.83736 19.5219 13.9185 20.7373 15.9276 20.4121C15.5526 21.4249 15.199 22.4217 14.9043 23.3758L14.9365 23.3652C14.9365 23.3652 14.7168 24.3353 14.7007 24.0901C14.4061 24.5912 14.0739 25.2415 13.731 25.9664C13.1095 25.5187 12.0434 24.6765 12.0434 24.6765L12.0487 24.6712ZM17.1652 36.8886C14.3257 38.8449 11.4808 42.0592 8.85557 44.2927C7.14648 42.5549 14.3364 33.2586 15.1561 29.9484C16.4473 30.5667 17.8885 31.5048 16.3348 30.0603C16.0187 29.7618 19.4476 32.5763 20.5995 33.3119C19.2119 34.4099 18.4618 36.201 17.1706 36.8886H17.1652ZM29.2681 30.8119C30.3075 32.7842 31.4647 36.185 32.0969 38.7809C29.236 36.782 27.409 35.0869 25.057 32.9388C25.2178 32.7895 25.3785 32.6402 25.5338 32.4909C25.2445 32.5176 26.1875 31.7394 26.075 31.9473C26.8304 31.4942 27.8215 30.8065 28.8449 30.0336C29.0806 30.4334 29.2574 30.8119 29.2574 30.8119H29.2681Z"
			fill="#101214"
		/>
	</svg>
);

export const ManageSection = () => {
	const intl = useIntl();

	/*** TEXT DECLARATIONS ***/
	const headerText = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Header',
		defaultMessage: 'Manage projects big and small',
	});
	const descriptionText = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Description',
		defaultMessage:
			'The most flexible and scalable tool in the market with granular controls over security, privacy, and workflows.',
	});

	const callToActionQuestionText = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Question',
		defaultMessage:
			'Interested in the benefits of centralized administration on the Atlassian cloud platform?',
	});

	const ctaText = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Cta',
		defaultMessage: 'Learn more here',
	});

	const ctaAriaLabel = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.CtaAriaLabel',
		defaultMessage: 'Learn more about centralized administration on the Atlassian cloud platform',
	});

	const cards: Card[] = [
		{
			icon: 'globe',
			text: intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.Globe.Text',
				defaultMessage: 'Global and project-level configuration',
			}),
		},
		{
			icon: 'workflow',
			text: intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.Wire.Text',
				defaultMessage: 'Customizable fields, workflows, and more',
			}),
		},
		{
			icon: 'foursquares',
			text: intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.FourSquare.Text',
				defaultMessage: 'Rich APIs',
			}),
		},
		{
			icon: 'openlock',
			text: (
				<FormattedMessage
					id="JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.Unlock.Text"
					defaultMessage="Permissions and <link>privacy controls</link>"
					values={{
						link: (str: ReactNode) => (
							<a
								className="manage-link"
								href={useLocalizedHref('/trust/data-protection?tab=privacy')}
							>
								{str}
							</a>
						),
					}}
				/>
			),
		},
		{
			icon: 'closedlock',
			text: (
				<FormattedMessage
					id="JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.Lock.Text"
					defaultMessage="Data residency, <link>data security & compliance</link>"
					values={{
						link: (str: ReactNode) => (
							<a className="manage-link" href={useLocalizedHref('/software/jira/security')}>
								{str}
							</a>
						),
					}}
				/>
			),
		},
		{
			icon: 'cog',
			text: (
				<FormattedMessage
					id="JiraSoftwareFeaturesPageV2.ValuePropSection.Icon.Cog.Text"
					defaultMessage="<link>SAML & SSO</link>"
					values={{
						link: (str: ReactNode) => {
							return (
								<a className="manage-link" href={useLocalizedHref('/software/guard')}>
									{str}
								</a>
							);
						},
					}}
				/>
			),
		},
	];

	/*** CSS DECLARATIONS ***/
	const headingTextStyles = css({
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '311px',
		padding: '0px',
		textAlign: 'center',
		isolation: 'isolate',
		margin: '8px auto 8px auto',
		gap: '8px',
		marginBlockStart: '50px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		h2: {
			margin: 0,
			position: 'relative',
			display: 'inline-block',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		p: {
			margin: 0,
		},
		[media.only.sm]: {
			maxWidth: '565px',
		},
		[media.above.sm]: {
			maxWidth: '767px',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			p: {
				padding: '8px 35px',
			},
		},
	});

	const cardContainerStyles = css({
		display: 'flex',
		padding: 0,
		margin: 0,
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'center',
		gap: '25px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.text-section p': {
			padding: '0 16px',
			fontWeight: 700,
			fontSize: '24px',
			lineHeight: '30px',
			color: wacToken('color.primary.black'),
			fontFamily: wacToken('font.charlie.display'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.manage-link': {
			color: wacToken('color.primary.blue'),
			fontWeight: 700,
		},
		[media.below.sm]: {
			flexDirection: 'column',
			alignItems: 'center',
			gap: '8px',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'> li': {
				maxWidth: '311px',
				height: '164px',
			},
		},
		[media.only.sm]: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'> li': {
				width: '311px',
				height: '164px',
			},
		},
		[media.above.md]: {
			gap: '38px',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'> li': {
				width: '351px',
				height: '164px',
			},
		},
	});

	const cardStyles = css({
		display: 'flex',
		justifyContent: 'center',
		listStyleType: 'none',
		padding: 0,
		margin: 0,
	});

	const dividerStyles = css({
		border: `3px solid ${wacToken('color.neutral.light200')}`,
		maxWidth: '311px',
		width: '100%',
		margin: '0 auto 0 auto',
		[media.above.md]: {
			maxWidth: '100%',
			margin: '0 auto 32px auto',
		},
	});

	const ctaStyles = css({
		position: 'relative',
		width: '100%',
		maxWidth: '311px',
		textAlign: 'center',
		margin: '0 auto 0 auto',
		paddingBlockEnd: '64px',
		[media.only.sm]: {
			maxWidth: '536px',
		},
		[media.above.md]: {
			maxWidth: '100%',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			p: {
				fontSize: '20px',
			},
		},
	});

	const eyelashStyles = css({
		position: 'absolute',
		insetInlineEnd: '-35px',
		insetBlockStart: '-35px',
		[media.below.sm]: {
			insetInlineEnd: '-23px',
			insetBlockStart: '-23px',
		},
	});

	const starsStyles = css({
		position: 'absolute',
		insetBlockStart: '-48px',
		[media.below.lg]: {
			display: 'none',
		},
	});

	return (
		<TypographyStyles>
			<section>
				<Row layout="12">
					<div css={headingTextStyles}>
						<div>
							<h2>
								{headerText}
								<div css={eyelashStyles}>
									<Eyelash />
								</div>
							</h2>
						</div>
						<p>{descriptionText}</p>
					</div>
				</Row>
				<Row layout="12">
					<ul css={cardContainerStyles}>
						{cards.map((card, index) => (
							<li key={index} css={cardStyles}>
								<ContentBlock
									iconName={card.icon}
									iconSize="small"
									isCentered={true}
									bodyText={card.text}
								/>
							</li>
						))}
					</ul>
				</Row>
				<Row layout="12">
					<hr role="presentation" aria-hidden="true" css={dividerStyles} />
				</Row>
				<Row layout="12">
					<div css={ctaStyles}>
						<div css={starsStyles}>
							<Stars />
						</div>
						<p>{callToActionQuestionText}</p>
						<Link
							href={useLocalizedHref('/trust/centralized-administration')}
							text={ctaText}
							aria-label={ctaAriaLabel}
						/>
					</div>
				</Row>
			</section>
		</TypographyStyles>
	);
};
